"use client"
import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { IUserRole } from '@/modules/users/domain/IUser'

// 'supervisor' (3) sigue en la lista, aunque no se asigne más, para poder
// quitárselo a las cuentas que lo tienen mientras se las pasa a uno de los dos
// perfiles nuevos.
const ALL_ROLES: IUserRole[] = [
	{ id: 2, role: 'owner' },
	{ id: 7, role: 'supervisor_operativo' },
	{ id: 8, role: 'supervisor_medico' },
	{ id: 9, role: 'supervisor_linea' },
	{ id: 3, role: 'supervisor' },
	{ id: 4, role: 'doctor' },
	{ id: 5, role: 'collaborator' },
]

const ROLE_LABELS: Record<string, string> = {
	owner: 'Owner',
	supervisor_operativo: 'Supervisor operativo',
	supervisor_medico: 'Supervisor médico',
	supervisor_linea: 'Supervisor de línea',
	supervisor: 'Supervisor (obsoleto)',
	doctor: 'Doctor',
	collaborator: 'Enfermero / Colaborador',
}

interface RoleEditorProps {
	uid: string
	currentRoles: IUserRole[]
	onRoleChange: (uid: string, roleId: number, action: 'add' | 'remove') => Promise<void>
}

export default function RoleEditor({ uid, currentRoles, onRoleChange }: RoleEditorProps) {
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState<number | null>(null)
	const ref = useRef<HTMLDivElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	/**
	 * Posición del panel en coordenadas de la ventana.
	 *
	 * El panel se abría con `absolute` dentro de la fila, y la tabla de usuarios
	 * es un contenedor con `overflow-scroll`: un hijo posicionado se recorta
	 * contra ese contenedor, así que el desplegable de roles salía cortado —de
	 * los seis roles se veía uno— y no había forma de llegar a los de abajo.
	 *
	 * Ahora se dibuja en un portal al final del body con `fixed`, o sea afuera
	 * del área que scrollea, y la posición se calcula desde el botón que lo
	 * abre. Es el mismo tipo de arreglo que el modal de base: si algo tiene que
	 * flotar por encima de la página, no puede vivir adentro de un contenedor
	 * que recorta.
	 */
	const [posicion, setPosicion] = useState<{ top: number; left: number } | null>(
		null,
	)

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			const objetivo = e.target as Node
			// El panel ya no es descendiente de `ref`, así que hay que preguntarle
			// también a él: sin esto, tildar un rol cerraba el desplegable.
			const adentro =
				ref.current?.contains(objetivo) || panelRef.current?.contains(objetivo)
			if (!adentro) setOpen(false)
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	/**
	 * Al scrollear o cambiar el tamaño se cierra, en vez de quedar flotando
	 * lejos de su fila: con `fixed` el panel no acompaña al contenido.
	 */
	useEffect(() => {
		if (!open) return
		const cerrar = () => setOpen(false)
		window.addEventListener('scroll', cerrar, true)
		window.addEventListener('resize', cerrar)
		return () => {
			window.removeEventListener('scroll', cerrar, true)
			window.removeEventListener('resize', cerrar)
		}
	}, [open])

	function alternar(e: React.MouseEvent<HTMLButtonElement>) {
		if (open) {
			setOpen(false)
			return
		}
		const caja = e.currentTarget.getBoundingClientRect()
		setPosicion({ top: caja.bottom + 4, left: caja.left + caja.width / 2 })
		setOpen(true)
	}

	const hasRole = (roleId: number) => currentRoles.some(r => r.id === roleId)

	const handleToggle = async (roleId: number) => {
		const action = hasRole(roleId) ? 'remove' : 'add'
		setLoading(roleId)
		try {
			await onRoleChange(uid, roleId, action)
		} finally {
			setLoading(null)
		}
	}

	const displayText = currentRoles.length > 0
		? currentRoles.map(r => ROLE_LABELS[r.role] || r.role).join(', ')
		// Sin rol no es un dato que falte: es lo que define a la nómina de
		// Trenes. Los roles son permisos de la app y esta gente no entra a la
		// app, se la evalúa.
		: 'Nómina Trenes'

	return (
		<div ref={ref} className="relative">
			<button
				onClick={alternar}
				className="text-left text-primary-700 underline cursor-pointer hover:text-primary-900 transition-colors"
				title="Editar roles"
			>
				{displayText}
			</button>
			{open && posicion && createPortal(
				<div
					ref={panelRef}
					style={{ top: posicion.top, left: posicion.left }}
					className="fixed z-[100] -translate-x-1/2 bg-white border border-grey-200 rounded-lg shadow-lg p-3 min-w-[220px] max-h-[60vh] overflow-y-auto"
				>
					<p className="text-xs font-semibold text-grey-500 mb-2">Editar roles</p>
					{ALL_ROLES.map((role) => (
						<label
							key={role.id}
							className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-grey-50 cursor-pointer text-sm text-grey-700"
						>
							<input
								type="checkbox"
								checked={hasRole(role.id)}
								disabled={loading !== null}
								onChange={() => handleToggle(role.id)}
								className="accent-primary-700 w-4 h-4"
							/>
							{ROLE_LABELS[role.role]}
							{loading === role.id && (
								<span className="ml-auto text-xs text-grey-400">...</span>
							)}
						</label>
					))}
				</div>,
				document.body,
			)}
		</div>
	)
}
